import {
  addDays,
  addHours,
  addMinutes,
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  formatDistanceToNow,
  getDate,
  getDay,
  isAfter,
  isBefore,
  isSameDay,
  isSameSecond,
  isSameYear,
  isValid,
  parse,
  set,
  setDefaultOptions,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMilliseconds,
  subMinutes,
} from 'date-fns';
import {
  formatInTimeZone,
  format as tzFormat,
  toZonedTime,
  fromZonedTime,
} from 'date-fns-tz';
import {enUS} from 'date-fns/locale/en-US';
import {cs as CS_CZ} from 'date-fns/locale/cs';
import type {Locale as DateFnsLocale} from 'date-fns';
import throttle from 'lodash/throttle';
import Onyx from 'react-native-onyx';
import type {ValueOf} from 'type-fest';
import CONST from '@src/CONST';
import ONYXKEYS from '@src/ONYXKEYS';
import {timezoneBackwardMap} from '@src/TIMEZONES';
import type {SelectedTimezone, Timezone} from '@src/types/onyx/UserData';
import {getFirebaseAuth} from './Firebase/FirebaseApp';
import * as CurrentDate from './actions/CurrentDate';
import * as Localize from './Localize';
import Log from './Log';
import {
  getDeviceTimezone as resolveDeviceTimezone,
  sanitizeTimezone,
} from './TimezoneUtils';

type CustomStatusTypes = ValueOf<typeof CONST.CUSTOM_STATUS_TYPES>;
type TimePeriod = 'AM' | 'PM';
type Locale = ValueOf<typeof CONST.LOCALES>;
type WeekDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

let timezone: Required<Timezone> = CONST.DEFAULT_TIME_ZONE;
Onyx.connect({
  key: ONYXKEYS.USER_DATA_LIST,
  callback: value => {
    // Safe to call getFirebaseAuth() here - Onyx callbacks run after app initialization
    const auth = getFirebaseAuth();
    if (!auth?.currentUser) {
      return;
    }
    const currentUserID = auth?.currentUser?.uid;
    const userDataTimezone = value?.[currentUserID]?.timezone;
    timezone = {
      // Validate on read, not only on resolve: a zone persisted by an older
      // build or synced from another device can be a name this engine refuses
      // as a `timeZone` option, and every formatter below would throw on it.
      selected: sanitizeTimezone(
        userDataTimezone?.selected ?? CONST.DEFAULT_TIME_ZONE.selected,
      ),
      automatic:
        userDataTimezone?.automatic ?? CONST.DEFAULT_TIME_ZONE.automatic,
    };
  },
});

let networkTimeSkew = 0;
Onyx.connect({
  key: ONYXKEYS.NETWORK,
  callback: value => (networkTimeSkew = value?.timeSkew ?? 0),
});

/**
 * Get the day of the week that the week starts on
 */
function getWeekStartsOn(): WeekDay {
  return CONST.WEEK_STARTS_ON;
}

/**
 * Get the day of the week that the week ends on
 */
function getWeekEndsOn(): WeekDay {
  const weekStartsOn = getWeekStartsOn();

  return weekStartsOn === 0 ? 6 : ((weekStartsOn - 1) as WeekDay);
}

/**
 * Gets the locale string and setting default locale for date-fns
 */
function setLocale(localeString: Locale) {
  switch (localeString) {
    case CONST.LOCALES.EN:
      setDefaultOptions({locale: enUS});
      break;
    case CONST.LOCALES.CS_CZ:
      setDefaultOptions({locale: CS_CZ});
      break;
    default:
      break;
  }
}

/**
 * Resolve an app locale to its `date-fns` locale object. Pass the result as the
 * `{locale}` option to `format()` so the output is localized deterministically,
 * without depending on the global `setDefaultOptions` default.
 */
function getDateFnsLocale(localeString: Locale): DateFnsLocale {
  return localeString === CONST.LOCALES.CS_CZ ? CS_CZ : enUS;
}

function getDayStartAndEndUTC(date: Date, tzInput: SelectedTimezone) {
  const tz = sanitizeTimezone(tzInput);
  const dateString = format(date, 'yyyy-MM-dd');
  const startOfDayUTC = fromZonedTime(`${dateString} 00:00:00`, tz).getTime();
  const endOfDayUTC = fromZonedTime(`${dateString} 23:59:59.999`, tz).getTime();
  return {startOfDayUTC, endOfDayUTC};
}

function getMonthStartAndEndUTC(
  date: Date,
  tzInput: string,
  untilToday: boolean,
) {
  const tz = sanitizeTimezone(tzInput);
  // Get the date in the session's timezone
  const zonedDate = toZonedTime(date, tz);

  // Start of the month in the session's timezone
  const startOfMonthDate = startOfMonth(zonedDate);
  const startOfMonthUTC = fromZonedTime(startOfMonthDate, tz).getTime();

  // End of the month in the session's timezone
  let endOfMonthDate = endOfMonth(zonedDate);

  if (untilToday) {
    const todayInTz = toZonedTime(new Date(), tz);
    const todayEndInTz = endOfDay(todayInTz);

    // If today is before the end of the month, use today as the end date
    if (todayEndInTz.getTime() < endOfMonthDate.getTime()) {
      endOfMonthDate = todayEndInTz;
    }
  }

  const endOfMonthUTC = fromZonedTime(endOfMonthDate, tz).getTime();

  return {startOfMonthUTC, endOfMonthUTC};
}
/**
 * Gets the user's stored time zone NVP and returns a localized
 * Date object for the given ISO-formatted datetime string
 */
function getLocalDateFromDatetime(
  locale: Locale,
  datetime?: string,
  selectedTimezone: SelectedTimezone = timezone.selected,
): Date {
  const currentSelectedTimezone = sanitizeTimezone(selectedTimezone);
  setLocale(locale);
  if (!datetime) {
    const res = toZonedTime(new Date(), currentSelectedTimezone);
    if (Number.isNaN(res.getTime())) {
      Log.warn(
        'DateUtils.getLocalDateFromDatetime: utcToZonedTime returned an invalid date. Returning current date.',
        {
          locale,
          datetime,
          currentSelectedTimezone,
        },
      );
      return new Date();
    }
    return res;
  }
  const parsedDatetime = new Date(`${datetime}Z`);
  return toZonedTime(parsedDatetime, currentSelectedTimezone);
}

/**
 * Checks if a given date is today in the specified time zone.
 *
 * @param date - The date to compare.
 * @param timeZone - The time zone to consider.
 * @returns True if the date is today; otherwise, false.
 */
function isToday(date: Date, timeZone: SelectedTimezone): boolean {
  const currentDate = new Date();
  const currentDateInTimeZone = toZonedTime(
    currentDate,
    sanitizeTimezone(timeZone),
  );
  return isSameDay(date, currentDateInTimeZone);
}

/**
 * Returns a localized time string based on the provided date, format string, and selected timezone.
 *
 * @param date - The date to be formatted. Can be a Date object, a string, or a timestamp.
 * @param - The format string to use for formatting the time. Defaults to a predefined short time format.
 * @returns The formatted time string localized to the selected timezone.
 */
function getLocalizedTime(
  date: Date | string | number | undefined | null,
  selectedTimezone: SelectedTimezone = timezone.selected,
  formatString: string = CONST.DATE.SHORT_TIME_FORMAT,
): string {
  if (!date) {
    return 'unknown';
  }
  return formatInTimeZone(
    date,
    sanitizeTimezone(selectedTimezone),
    formatString,
  );
}

function getLocalizedDay(
  date: Date | string | number | undefined | null,
  selectedTimezone: SelectedTimezone = timezone.selected,
  formatString: string = CONST.DATE.SHORT_DATE_FORMAT,
): string {
  if (!date) {
    return 'unknown';
  }
  const day = toZonedTime(date, sanitizeTimezone(selectedTimezone));
  return format(day, formatString);
}

/**
 * Get the device's time zone, validated against what this engine will accept
 * back as a `timeZone` option (see `TimezoneUtils`).
 */
const getDeviceTimezone = (): SelectedTimezone => resolveDeviceTimezone();

/**
 * Checks if a given date is tomorrow in the specified time zone.
 *
 * @param date - The date to compare.
 * @param timeZone - The time zone to consider.
 * @returns True if the date is tomorrow; otherwise, false.
 */
function isTomorrow(date: Date, timeZone: SelectedTimezone): boolean {
  const currentDate = new Date();
  const tomorrow = addDays(currentDate, 1); // Get the date for tomorrow in the current time zone
  const tomorrowInTimeZone = toZonedTime(tomorrow, sanitizeTimezone(timeZone));
  return isSameDay(date, tomorrowInTimeZone);
}

/**
 * Checks if a given date is yesterday in the specified time zone.
 *
 * @param date - The date to compare.
 * @param timeZone - The time zone to consider.
 * @returns True if the date is yesterday; otherwise, false.
 */
function isYesterday(date: Date, timeZone: SelectedTimezone): boolean {
  const currentDate = new Date();
  const yesterday = subDays(currentDate, 1); // Get the date for yesterday in the current time zone
  const yesterdayInTimeZone = toZonedTime(
    yesterday,
    sanitizeTimezone(timeZone),
  );
  return isSameDay(date, yesterdayInTimeZone);
}

/**
 * Formats an ISO-formatted datetime string to local date and time string
 *
 * e.g.
 *
 * Jan 20 at 5:30 PM          within the past year
 * Jan 20, 2019 at 5:30 PM    anything over 1 year ago
 */
function datetimeToCalendarTime(
  locale: Locale,
  datetime: string,
  includeTimeZone = false,
  currentSelectedTimezone: SelectedTimezone = timezone.selected,
  isLowercase = false,
): string {
  const date = getLocalDateFromDatetime(
    locale,
    datetime,
    currentSelectedTimezone,
  );
  const tz = includeTimeZone ? ' [UTC]Z' : '';
  let todayAt = Localize.translate(locale, 'common.todayAt');
  let tomorrowAt = Localize.translate(locale, 'common.tomorrowAt');
  let yesterdayAt = Localize.translate(locale, 'common.yesterdayAt');
  const at = Localize.translate(locale, 'common.conjunctionAt');
  const weekStartsOn = getWeekStartsOn();

  const startOfCurrentWeek = startOfWeek(new Date(), {weekStartsOn});
  const endOfCurrentWeek = endOfWeek(new Date(), {weekStartsOn});

  if (isLowercase) {
    todayAt = todayAt.toLowerCase();
    tomorrowAt = tomorrowAt.toLowerCase();
    yesterdayAt = yesterdayAt.toLowerCase();
  }

  if (isToday(date, currentSelectedTimezone)) {
    return `${todayAt} ${format(date, CONST.DATE.LOCAL_TIME_FORMAT)}${tz}`;
  }
  if (isTomorrow(date, currentSelectedTimezone)) {
    return `${tomorrowAt} ${format(date, CONST.DATE.LOCAL_TIME_FORMAT)}${tz}`;
  }
  if (isYesterday(date, currentSelectedTimezone)) {
    return `${yesterdayAt} ${format(date, CONST.DATE.LOCAL_TIME_FORMAT)}${tz}`;
  }
  if (date >= startOfCurrentWeek && date <= endOfCurrentWeek) {
    return `${format(date, CONST.DATE.MONTH_DAY_ABBR_FORMAT)} ${at} ${format(
      date,
      CONST.DATE.LOCAL_TIME_FORMAT,
    )}${tz}`;
  }
  return `${format(date, CONST.DATE.MONTH_DAY_YEAR_ABBR_FORMAT)} ${at} ${format(
    date,
    CONST.DATE.LOCAL_TIME_FORMAT,
  )}${tz}`;
}

/**
 * Converts an ISO-formatted datetime string into a localized string representation
 * that's relative to current moment in time.
 *
 * e.g.
 *
 * < 1 minute ago       within the past minute
 * 12 minutes ago       within the past hour
 * 1 hour ago           within the past day
 * 3 days ago           within the past month
 * Jan 20               within the past year
 * Jan 20, 2019         anything over 1 year
 */
function datetimeToRelative(locale: Locale, datetime: string): string {
  const date = getLocalDateFromDatetime(locale, datetime);
  return formatDistanceToNow(date, {addSuffix: true});
}

/**
 * Gets the zone abbreviation from the date
 *
 * e.g.
 *
 * PST
 * EST
 * GMT +07  -  For GMT timezone
 *
 * @param datetime
 * @param selectedTimezone
 * @returns
 */
function getZoneAbbreviation(
  datetime: string | Date,
  selectedTimezone: SelectedTimezone,
): string {
  return formatInTimeZone(datetime, sanitizeTimezone(selectedTimezone), 'zzz');
}

/**
 * Format date to a long date format with weekday
 *
 * @returns Sunday, July 9, 2023
 */
function formatToLongDateWithWeekday(datetime: string | Date): string {
  return format(new Date(datetime), CONST.DATE.LONG_DATE_FORMAT_WITH_WEEKDAY);
}

/**
 * Format date to a weekday format
 *
 * @returns Sunday
 */
function formatToDayOfWeek(datetime: Date): string {
  return format(datetime, CONST.DATE.WEEKDAY_TIME_FORMAT);
}

/**
 * Format date to a local time
 *
 * @returns 2:30 PM
 */
function formatToLocalTime(datetime: string | Date): string {
  return format(new Date(datetime), CONST.DATE.LOCAL_TIME_FORMAT);
}

const THREE_HOURS = 1000 * 60 * 60 * 3;

/**
 * A throttled version of a function that updates the current date in Onyx store
 */
const updateCurrentDate = throttle(() => {
  const currentDate = format(new Date(), CONST.DATE.FNS_FORMAT_STRING);
  CurrentDate.setCurrentDate(currentDate);
}, THREE_HOURS);

/**
 * Initialises the event listeners that trigger the current date update
 */
function startCurrentDateUpdater() {
  const trackedEvents = ['mousemove', 'touchstart', 'keydown', 'scroll'];
  trackedEvents.forEach(eventName => {
    document.addEventListener(eventName, updateCurrentDate);
  });
}

function getCurrentTimezone(): Required<Timezone> {
  const currentTimezone = resolveDeviceTimezone();
  if (timezone.automatic && timezone.selected !== currentTimezone) {
    return {...timezone, selected: currentTimezone};
  }
  return timezone;
}

/**
 * @returns [January, Fabruary, March, April, May, June, July, August, ...]
 */
function getMonthNames(preferredLocale: Locale): string[] {
  if (preferredLocale) {
    setLocale(preferredLocale);
  }
  const fullYear = new Date().getFullYear();
  const monthsArray = eachMonthOfInterval({
    start: new Date(fullYear, 0, 1), // January 1st of the current year
    end: new Date(fullYear, 11, 31), // December 31st of the current year
  });

  // eslint-disable-next-line rulesdir/prefer-underscore-method
  return monthsArray.map(monthDate =>
    format(monthDate, CONST.DATE.MONTH_FORMAT),
  );
}

/**
 * @returns Localized standalone abbreviated month names, e.g. [Jan, Feb, ...]
 */
function getMonthShortNames(preferredLocale: Locale): string[] {
  if (preferredLocale) {
    setLocale(preferredLocale);
  }
  const fullYear = new Date().getFullYear();
  const monthsArray = eachMonthOfInterval({
    start: new Date(fullYear, 0, 1),
    end: new Date(fullYear, 11, 31),
  });

  // eslint-disable-next-line rulesdir/prefer-underscore-method
  return monthsArray.map(monthDate =>
    format(monthDate, CONST.DATE.SHORT_MONTH_FORMAT),
  );
}

/**
 * @returns [Monday, Thuesday, Wednesday, ...]
 */
function getDaysOfWeek(preferredLocale: Locale): string[] {
  if (preferredLocale) {
    setLocale(preferredLocale);
  }
  const weekStartsOn = getWeekStartsOn();
  const startOfCurrentWeek = startOfWeek(new Date(), {weekStartsOn});
  const endOfCurrentWeek = endOfWeek(new Date(), {weekStartsOn});
  const daysOfWeek = eachDayOfInterval({
    start: startOfCurrentWeek,
    end: endOfCurrentWeek,
  });

  // eslint-disable-next-line rulesdir/prefer-underscore-method
  return daysOfWeek.map(date => format(date, 'eeee'));
}

/**
 * Localized abbreviated weekday names in absolute (Sunday-first) order, e.g.
 * [Sun, Mon, Tue, ...]. Callers rotate to their own week start. Used as the
 * single source of truth for every calendar's weekday header.
 */
function getDayShortNames(preferredLocale: Locale): string[] {
  if (preferredLocale) {
    setLocale(preferredLocale);
  }
  const startOfCurrentWeek = startOfWeek(new Date(), {weekStartsOn: 0});
  const endOfCurrentWeek = endOfWeek(new Date(), {weekStartsOn: 0});
  const daysOfWeek = eachDayOfInterval({
    start: startOfCurrentWeek,
    end: endOfCurrentWeek,
  });

  // eslint-disable-next-line rulesdir/prefer-underscore-method
  return daysOfWeek.map(date => format(date, 'eee'));
}

// Used to throttle automatic timezone updates (e.g. on app foreground).
const TIMEZONE_UPDATE_THROTTLE_MINUTES = 5;
// Seed in the past so the first update after launch/login is allowed through.
let lastUpdatedTimezoneTime = subMinutes(
  new Date(),
  TIMEZONE_UPDATE_THROTTLE_MINUTES + 1,
);

function canUpdateTimezone(): boolean {
  const currentTime = new Date();
  const throttleCutoff = subMinutes(
    currentTime,
    TIMEZONE_UPDATE_THROTTLE_MINUTES,
  );
  // Compare the last updated time with the throttle window.
  return isBefore(lastUpdatedTimezoneTime, throttleCutoff);
}

function setTimezoneUpdated() {
  lastUpdatedTimezoneTime = new Date();
}

/**
 * Get the UNIX timestamp in microseconds, with millisecond precision.
 */
function getMicroseconds(): number {
  return Date.now() * CONST.MICROSECONDS_PER_MS;
}

function getDBTimeFromDate(date: Date): string {
  return date.toISOString().replace('T', ' ').replace('Z', '');
}

/**
 * Convert the given timestamp to the "yyyy-MM-dd HH:mm:ss" format, as expected by the database
 *
 * @param [timestamp] the given timestamp (if omitted, defaults to the current time)
 */
function getDBTime(timestamp: string | number = ''): string {
  const datetime = timestamp ? new Date(timestamp) : new Date();
  return getDBTimeFromDate(datetime);
}

/**
 * The current time on the server's clock (epoch ms): this device's clock
 * corrected by the offset measured from kiroku-api's bootstrap responses (see
 * `HttpUtils`). Use it for timestamps the server orders by, so a phone whose
 * clock is minutes off doesn't scramble a shared timeline (Sessions v2 RFC
 * §5.5). Before the first measurement it is the device clock.
 */
function getServerTime(): number {
  return Date.now() + networkTimeSkew;
}

/**
 * Returns the current server time in the format expected by the database
 */
function getDBTimeWithSkew(): string {
  return getDBTime(getServerTime());
}

function subtractMillisecondsFromDateTime(
  dateTime: string,
  milliseconds: number,
): string {
  const date = fromZonedTime(dateTime, 'UTC');
  const newTimestamp = subMilliseconds(date, milliseconds).valueOf();

  return getDBTime(newTimestamp);
}

/**
 * @param isoTimestamp example: 2023-05-16 05:34:14.388
 * @returns example: 2023-05-16
 */
function getDateStringFromISOTimestamp(isoTimestamp: string): string {
  if (!isoTimestamp) {
    return '';
  }

  const [dateString] = isoTimestamp.split(' ');
  return dateString;
}

/**
 * returns {string} example: 2023-05-16 05:34:14
 */
function getThirtyMinutesFromNow(): string {
  const date = addMinutes(new Date(), 30);
  return format(date, 'yyyy-MM-dd HH:mm:ss');
}

/**
 * returns {string} example: 2023-05-16 05:34:14
 */
function getOneHourFromNow(): string {
  const date = addHours(new Date(), 1);
  return format(date, 'yyyy-MM-dd HH:mm:ss');
}

/**
 * returns {string} example: 2023-05-16 05:34:14
 */
function getEndOfToday(): string {
  const date = endOfDay(new Date());
  return format(date, 'yyyy-MM-dd HH:mm:ss');
}

/**
 * returns {string} example: 2023-05-16 05:34:14
 */
function getOneWeekFromNow(): string {
  const date = addDays(new Date(), 7);
  return format(date, 'yyyy-MM-dd HH:mm:ss');
}

/**
 * param {string} dateTimeString
 * returns {string} example: 2023-05-16
 */
function extractDate(dateTimeString: string): string {
  if (!dateTimeString) {
    return '';
  }
  if (dateTimeString === 'never') {
    return '';
  }
  const date = new Date(dateTimeString);
  return format(date, 'yyyy-MM-dd');
}

/**
 * param {string} dateTimeString
 * returns {string} example: 11:10 PM
 */
function extractTime12Hour(dateTimeString: string): string {
  if (!dateTimeString || dateTimeString === 'never') {
    return '';
  }
  const date = new Date(dateTimeString);
  return format(date, 'hh:mm a');
}

/**
 * param {string} dateTimeString
 * returns {string} example: 2023-05-16 11:10 PM
 */
function formatDateTimeTo12Hour(dateTimeString: string): string {
  if (!dateTimeString) {
    return '';
  }
  const date = new Date(dateTimeString);
  return format(date, 'yyyy-MM-dd hh:mm a');
}

/**
 * param {string} type - one of the values from CONST.CUSTOM_STATUS_TYPES
 * returns {string} example: 2023-05-16 11:10:00 or ''
 */
function getDateFromStatusType(type: CustomStatusTypes): string {
  switch (type) {
    case CONST.CUSTOM_STATUS_TYPES.THIRTY_MINUTES:
      return getThirtyMinutesFromNow();
    case CONST.CUSTOM_STATUS_TYPES.ONE_HOUR:
      return getOneHourFromNow();
    case CONST.CUSTOM_STATUS_TYPES.AFTER_TODAY:
      return getEndOfToday();
    case CONST.CUSTOM_STATUS_TYPES.AFTER_WEEK:
      return getOneWeekFromNow();
    case CONST.CUSTOM_STATUS_TYPES.NEVER:
      return CONST.CUSTOM_STATUS_TYPES.NEVER;
    default:
      return '';
  }
}

/**
 * param {string} data - either a value from CONST.CUSTOM_STATUS_TYPES or a dateTime string in the format YYYY-MM-DD HH:mm
 * returns {string} example: 2023-05-16 11:10 PM or 'Today'
 */
function getLocalizedTimePeriodDescription(data: string): string {
  const {translateLocal} = Localize;
  switch (data) {
    case getEndOfToday():
      return translateLocal('timePeriods.afterToday');
    case CONST.CUSTOM_STATUS_TYPES.NEVER:
    case '':
      return translateLocal('timePeriods.never');
    default:
      return formatDateTimeTo12Hour(data);
  }
}

/**
 * receive date like 2020-05-16 05:34:14 and format it to show in string like "Until 05:34 PM"
 */
function getStatusUntilDate(inputDate: string): string {
  if (!inputDate) {
    return '';
  }
  const {translateLocal} = Localize;

  const input = new Date(inputDate);
  const now = new Date();
  const endOfToday = endOfDay(now);

  // If the date is equal to the end of today
  if (isSameDay(input, endOfToday)) {
    return translateLocal('timePeriods.untilTomorrow');
  }

  // If it's a time on the same date
  if (isSameDay(input, now)) {
    return translateLocal('timePeriods.untilTime', {
      time: format(input, CONST.DATE.LOCAL_TIME_FORMAT),
    });
  }

  // If it's further in the future than tomorrow but within the same year
  if (isAfter(input, now) && isSameYear(input, now)) {
    return translateLocal('timePeriods.untilTime', {
      time: format(
        input,
        `${CONST.DATE.SHORT_DATE_FORMAT} ${CONST.DATE.LOCAL_TIME_FORMAT}`,
      ),
    });
  }

  // If it's in another year
  return translateLocal('timePeriods.untilTime', {
    time: format(
      input,
      `${CONST.DATE.FNS_FORMAT_STRING} ${CONST.DATE.LOCAL_TIME_FORMAT}`,
    ),
  });
}

/**
 * Update the time for a given date.
 *
 * param {string} updatedTime - Time in "hh:mm A" or "HH:mm:ss" or "yyyy-MM-dd HH:mm:ss" format.
 * param {string} inputDateTime - Date in "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD" format.
 * returns {string} - Date with updated time in "YYYY-MM-DD HH:mm:ss" format.
 */
const combineDateAndTime = (
  updatedTime: string,
  inputDateTime: string,
): string => {
  if (!updatedTime || !inputDateTime) {
    return '';
  }

  let parsedTime: Date | null = null;
  if (updatedTime.includes('-')) {
    // it's in "yyyy-MM-dd HH:mm:ss" format
    const tempTime = parse(updatedTime, 'yyyy-MM-dd HH:mm:ss', new Date());
    if (isValid(tempTime)) {
      parsedTime = tempTime;
    }
  } else if (updatedTime.includes(':')) {
    // it's in "hh:mm a" format
    const tempTime = parse(updatedTime, 'hh:mm a', new Date());
    if (isValid(tempTime)) {
      parsedTime = tempTime;
    }
  }

  if (!parsedTime) {
    return '';
  }

  let parsedDateTime: Date | null = null;
  if (inputDateTime.includes(':')) {
    // Check if it includes time
    const tempDateTime = parse(
      inputDateTime,
      'yyyy-MM-dd HH:mm:ss',
      new Date(),
    );
    if (isValid(tempDateTime)) {
      parsedDateTime = tempDateTime;
    }
  } else {
    const tempDateTime = parse(inputDateTime, 'yyyy-MM-dd', new Date());
    if (isValid(tempDateTime)) {
      parsedDateTime = tempDateTime;
    }
  }

  if (!parsedDateTime) {
    return '';
  }

  const updatedDateTime = set(parsedDateTime, {
    hours: parsedTime.getHours(),
    minutes: parsedTime.getMinutes(),
    seconds: parsedTime.getSeconds(),
  });

  return format(updatedDateTime, 'yyyy-MM-dd HH:mm:ss');
};

/**
 * param {String} dateTime in 'HH:mm:ss' format
 * returns {Object}
 * example {hour: '11', minute: '10', period: 'AM'}
 */
function get12HourTimeObjectFromDate(dateTime: string): {
  hour: string;
  minute: string;
  period: string;
} {
  if (!dateTime) {
    return {
      hour: '12',
      minute: '00',
      period: 'PM',
    };
  }
  const parsedTime = parse(dateTime, 'hh:mm a', new Date());
  return {
    hour: format(parsedTime, 'hh'),
    minute: format(parsedTime, 'mm'),
    period: format(parsedTime, 'a').toUpperCase(),
  };
}

/**
 * param {String} timeString
 * returns {String}
 * example getTimePeriod('11:10 PM') // 'PM'
 */
function getTimePeriod(timeString: string): TimePeriod {
  const parts = timeString.split(' ');
  return parts[1] as TimePeriod;
}

/**
 * param {String} dateTimeStringFirst // YYYY-MM-DD HH:mm:ss
 * param {String} dateTimeStringSecond // YYYY-MM-DD HH:mm:ss
 * returns {Boolean}
 */
function areDatesIdentical(
  dateTimeStringFirst: string,
  dateTimeStringSecond: string,
): boolean {
  const date1 = parse(dateTimeStringFirst, 'yyyy-MM-dd HH:mm:ss', new Date());
  const date2 = parse(dateTimeStringSecond, 'yyyy-MM-dd HH:mm:ss', new Date());

  return isSameSecond(date1, date2);
}

/**
 * Checks if the time input is at least one minute in the future.
 * param {String} timeString: '04:24 AM'
 * param {String} dateTimeString: '2023-11-14 14:24:00'
 * returns {Boolean}
 */
const isTimeAtLeastOneMinuteInFuture = ({
  timeString,
  dateTimeString,
}: {
  timeString?: string;
  dateTimeString: string;
}): boolean => {
  let dateToCheck = dateTimeString;
  if (timeString) {
    //  return false;
    // Parse the hour and minute from the time input
    const [hourStr] = timeString.split(/[:\s]+/);
    const hour = parseInt(hourStr, 10);

    if (hour === 0) {
      return false;
    }

    dateToCheck = combineDateAndTime(timeString, dateTimeString);
  }

  // Get current date and time
  const now = new Date();

  // Check if the combinedDate is at least one minute later than the current date and time
  return isAfter(new Date(dateToCheck), addMinutes(now, 1));
};

/**
 * Checks if the input date is in the future compared to the reference date.
 * param {Date} inputDate - The date to validate.
 * param {Date} referenceDate - The date to compare against.
 * returns {string} - Returns an error key if validation fails, otherwise an empty string.
 */
const getDayValidationErrorKey = (inputDate: Date): string => {
  if (!inputDate) {
    return '';
  }

  if (isAfter(startOfDay(new Date()), startOfDay(inputDate))) {
    return 'common.error.invalidDateShouldBeFuture';
  }
  return '';
};

/**
 * Checks if the input time is at least one minute in the future compared to the reference time.
 * param {Date} inputTime - The time to validate.
 * param {Date} referenceTime - The time to compare against.
 * returns {string} - Returns an error key if validation fails, otherwise an empty string.
 */
const getTimeValidationErrorKey = (inputTime: Date): string => {
  const timeNowPlusOneMinute = addMinutes(new Date(), 1);
  if (isBefore(inputTime, timeNowPlusOneMinute)) {
    return 'common.error.invalidTimeShouldBeFuture';
  }
  return '';
};

/**
 *
 * Get a date and format this date using the UTC timezone.
 * param datetime
 * param dateFormat
 * returns If the date is valid, returns the formatted date with the UTC timezone, otherwise returns an empty string.
 */
function formatWithUTCTimeZone(
  datetime: string,
  dateFormat: string = CONST.DATE.FNS_FORMAT_STRING,
): string {
  const date = new Date(datetime);

  if (isValid(date)) {
    return tzFormat(toZonedTime(date, 'UTC'), dateFormat);
  }

  return '';
}

/**
 *
 * @param timezone
 * function format unsupported timezone to supported timezone
 * @returns Timezone
 */
function formatToSupportedTimezone(timezoneInput: Timezone): Timezone {
  if (!timezoneInput?.selected) {
    return timezoneInput;
  }
  return {
    selected:
      timezoneBackwardMap[timezoneInput.selected] ?? timezoneInput.selected,
    automatic: timezoneInput.automatic,
  };
}

/**
 * Returns the last business day of given date month
 *
 * param {Date} inputDate
 * returns {number}
 */
function getLastBusinessDayOfMonth(inputDate: Date): number {
  let currentDate = endOfMonth(inputDate);
  const dayOfWeek = getDay(currentDate);

  if (dayOfWeek === 0) {
    currentDate = subDays(currentDate, 2);
  } else if (dayOfWeek === 6) {
    currentDate = subDays(currentDate, 1);
  }

  return getDate(currentDate);
}

const DateUtils = {
  areDatesIdentical,
  canUpdateTimezone,
  combineDateAndTime,
  datetimeToCalendarTime,
  datetimeToRelative,
  extractDate,
  extractTime12Hour,
  formatDateTimeTo12Hour,
  formatToDayOfWeek,
  formatToLocalTime,
  formatToLongDateWithWeekday,
  formatToSupportedTimezone,
  formatWithUTCTimeZone,
  get12HourTimeObjectFromDate,
  getCurrentTimezone,
  getDBTime,
  getDBTimeWithSkew,
  getServerTime,
  getDateFromStatusType,
  getDateFnsLocale,
  getDateStringFromISOTimestamp,
  getDayValidationErrorKey,
  getDaysOfWeek,
  getDayShortNames,
  getDeviceTimezone,
  getDayStartAndEndUTC,
  getEndOfToday,
  getMonthStartAndEndUTC,
  getLastBusinessDayOfMonth,
  getLocalDateFromDatetime,
  getLocalizedTime,
  getLocalizedDay,
  getLocalizedTimePeriodDescription,
  getMicroseconds,
  getMonthNames,
  getMonthShortNames,
  getOneHourFromNow,
  getOneWeekFromNow,
  getStatusUntilDate,
  getThirtyMinutesFromNow,
  getTimePeriod,
  getTimeValidationErrorKey,
  getWeekEndsOn,
  getWeekStartsOn,
  getZoneAbbreviation,
  isTimeAtLeastOneMinuteInFuture,
  isToday,
  isTomorrow,
  isYesterday,
  setLocale,
  setTimezoneUpdated,
  startCurrentDateUpdater,
  subtractMillisecondsFromDateTime,
};

export default DateUtils;
